// IM-2595: nav composition for the appbar, kept pure + React-free so it can be
// unit tested without dragging the appbar's MUI/scss imports into vitest (mirrors
// allowed-links.ts). Fed the already permission-filtered links (see
// selectAllowedLinks) and composed on top of them.

// Minimal shape the helpers need — appbar's AppBarlink satisfies it. Kept generic
// so callers get their own link type back unchanged.
type LabelledLink = { label: string };

export type NavGroup = { label: string; childLabels: string[] };

export type NavEntry<T> = { type: 'link'; link: T } | { type: 'group'; label: string; links: T[] };

// IM-2595: when `restrict` is OFF returns the SAME array reference untouched
// (production behaviour — feature-off entries stay, greyed by the render layer).
// When ON, drops any link whose feature flag is OFF, so a restricted nav shows
// only links that are BOTH permitted (upstream, selectAllowedLinks) AND
// feature-enabled.
export const selectVisibleNavLinks = <T extends LabelledLink>(
  links: T[],
  restrict: boolean,
  isLabelEnabled: (label: string) => boolean,
): T[] => (restrict ? links.filter((link) => isLabelEnabled(link.label)) : links);

// Builds the ordered top-nav entries from the given links: a link whose label
// belongs to a NAV_GROUP is nested under that group's dropdown; everything else
// is a flat link. A group emits once, at the position of its first child, with
// children in childLabels order. A group whose children are all absent from
// `links` never emits (no empty dropdown) — the collapse is inherent, since a
// group is only reached when one of its children is encountered in `links`.
export const buildNavEntries = <T extends LabelledLink>(
  links: T[],
  groups: NavGroup[],
): NavEntry<T>[] => {
  const entries: NavEntry<T>[] = [];
  const emittedGroups = new Set<string>();

  links.forEach((link) => {
    const group = groups.find((g) => g.childLabels.includes(link.label));
    if (!group) {
      entries.push({ type: 'link', link });
      return;
    }
    if (emittedGroups.has(group.label)) return;
    emittedGroups.add(group.label);
    entries.push({
      type: 'group',
      label: group.label,
      links: group.childLabels
        .map((childLabel) => links.find((l) => l.label === childLabel))
        .filter((l): l is T => Boolean(l)),
    });
  });

  return entries;
};
